import { currentLocale, fmt, tr } from "../i18n/index.tsx";
import { isMock } from "./mode.ts";

/** Error from the console API: `{ error: { code, message } }` plus the HTTP status. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type Listener = () => void;
const unauthListeners = new Set<Listener>();
/** Called whenever any request answers 401 `unauthenticated` (the session is gone). */
export function onUnauthenticated(fn: Listener) {
  unauthListeners.add(fn);
  return () => void unauthListeners.delete(fn);
}
const blockedListeners = new Set<(message: string) => void>();
/** Called when a logged-in request answers 403 `ip_not_allowed` (this network is outside the console IP allowlist). */
export function onIpNotAllowed(fn: (message: string) => void) {
  blockedListeners.add(fn);
  return () => void blockedListeners.delete(fn);
}
/** Auth calls that report ip_not_allowed as a form error instead of the full-page state. */
const FORM_PATHS = ["/auth/login", "/auth/mfa", "/invites/", "/apply/"];

export type Query = Record<string, string | number | undefined | null>;

export function qs(params: Query = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

async function realFetch(method: string, path: string, body?: unknown): Promise<unknown> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-ICN-Console"] = "1"; // CSRF guard: required on every non-GET request
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "Can't reach the server. Check your connection and try again.");
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? `Request failed (${res.status})`);
  }
  return data;
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    if (isMock) {
      const { mockFetch } = await import("./mock.ts");
      return (await mockFetch(method, path, body)) as T;
    }
    return (await realFetch(method, path, body)) as T;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && e.code === "unauthenticated") unauthListeners.forEach((fn) => fn());
    if (e instanceof ApiError && e.code === "ip_not_allowed" && !FORM_PATHS.some((p) => path.startsWith(p))) blockedListeners.forEach((fn) => fn(e.message));
    throw e;
  }
}

export const api = {
  get: <T>(path: string, params?: Query) => request<T>("GET", path + qs(params)),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** CSV export. Real mode: a plain download link. Mock mode: the mock builds the file in the browser. */
export async function downloadCsv(path: string, params: Query, filename: string) {
  if (!isMock) {
    const a = document.createElement("a");
    a.href = `/api${path}${qs(params)}`;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    return;
  }
  const { mockCsv } = await import("./mock.ts");
  const blob = new Blob([mockCsv(path, params)], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Codes whose own text says everything; other codes add the server's (English) detail outside English. */
const SPECIFIC = new Set([
  "network", "unauthenticated", "mfa_required", "invalid_credentials", "rate_limited", "ip_not_allowed", "no_access",
  "login_required", "csrf", "insufficient_balance", "payouts_paused",
]);

/** A human message for any error, in the console language. English shows the server's own sentence when it has one. */
export function errorMessage(e: unknown): string {
  const errs = tr().common.errors as Record<string, string>;
  if (!(e instanceof ApiError)) return errs.generic ?? "";
  const msg = e.message?.trim();
  const detail = msg && !/^Request failed/.test(msg) ? msg.charAt(0).toUpperCase() + msg.slice(1) : "";
  const known = errs[e.code] ?? (tr().vault.errors as Record<string, string>)[e.code];
  if (currentLocale() === "en") return detail || known || errs.generic || "";
  if (known && (SPECIFIC.has(e.code) || !detail)) return known;
  if (detail) return fmt(errs.withDetail ?? "{message} ({detail})", { message: known ?? errs.generic ?? "", detail });
  return errs.generic ?? "";
}

export const isCode = (e: unknown, ...codes: string[]) => e instanceof ApiError && codes.includes(e.code);
